/**
 * Session handling shared by both apps. The real session lives server-side (HttpOnly cookie
 * set by the platform API); the browser only keeps the identity returned by login/restore in
 * memory. After a reload the provider asks the API once (`restore`) and shows a loading screen
 * until it knows; a 401 from any later call (`onExpired`) signs the user out with a toast.
 *
 *   export const { SessionProvider, useSession } = createSession<Session>(api);
 */
import { useI18n } from '@platform/i18n';
import { LoaderCircleIcon } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from '../components/sonner';

export interface Credentials {
  username: string;
  password: string;
}

export interface SessionApi<S> {
  login(username: string, password: string): Promise<S>;
  logout(): Promise<void>;
  /** The current server session after a page reload, or null when signed out. */
  restoreSession(): Promise<S | null>;
  /** Subscribes to "session missing or expired" (any 401); returns the unsubscribe function. */
  onSessionExpired?(listener: () => void): () => void;
}

export interface SessionContextValue<S> {
  session: S | null;
  login: (credentials: Credentials) => Promise<void>;
  logout: () => Promise<void>;
  /** Admission failed without proving a signed-in session for this app. */
  admissionError: unknown;
  retryAdmission: () => void;
}

/** Full-page loading indicator while the session is restored. */
function SessionLoading() {
  const { t } = useI18n();
  return (
    <main className="flex min-h-dvh items-center justify-center" aria-busy="true">
      <LoaderCircleIcon aria-hidden className="size-6 animate-spin text-muted-foreground motion-reduce:animate-none" />
      <span className="sr-only">{t('common.loading')}</span>
    </main>
  );
}

export function createSession<S>(api: SessionApi<S>) {
  const Context = createContext<SessionContextValue<S> | null>(null);

  function SessionProvider({ children }: { children: ReactNode }) {
    const { t } = useI18n();
    const [session, setSession] = useState<S | null>(null);
    const [restoring, setRestoring] = useState(true);
    const [admissionError, setAdmissionError] = useState<unknown>();
    const [attempt, setAttempt] = useState(0);
    // A late admission response cannot undo logout, expiry or a newer explicit login.
    const generation = useRef(0);
    const retryAdmission = useCallback(() => { setRestoring(true); setAttempt((current) => current + 1); }, []);

    useEffect(() => {
      let active = true;
      const current = ++generation.current;
      api.restoreSession().then(
        (restored) => {
          if (!active || current !== generation.current) return;
          setSession(restored);
          setAdmissionError(undefined);
          setRestoring(false);
        },
        (error: unknown) => {
          if (active && current === generation.current) { setSession(null); setAdmissionError(error); setRestoring(false); }
        },
      );
      return () => {
        active = false;
      };
    }, [attempt]);

    const signedIn = useRef(false);
    useEffect(() => {
      signedIn.current = session !== null;
    }, [session]);
    useEffect(
      () =>
        api.onSessionExpired?.(() => {
          generation.current += 1;
          if (signedIn.current) {
            signedIn.current = false;
            toast.info(t('auth.sessionExpired'));
          }
          setSession(null);
          setAdmissionError(undefined);
          setRestoring(false);
        }),
      [t],
    );

    const login = useCallback(async ({ username, password }: Credentials) => {
      const current = ++generation.current;
      setAdmissionError(undefined);
      try {
        const next = await api.login(username, password);
        if (current !== generation.current) return;
        signedIn.current = true;
        setSession(next);
      } finally {
        if (current === generation.current) setRestoring(false);
      }
    }, []);
    const logout = useCallback(async () => {
      const current = ++generation.current;
      // An intentional sign-out does not need a second expiry notice from in-flight calls.
      signedIn.current = false;
      try {
        await api.logout();
      } finally {
        if (current === generation.current) {
          setAdmissionError(undefined);
          setSession(null);
          setRestoring(false);
        }
      }
    }, []);
    const value = useMemo(() => ({ session, login, logout, admissionError, retryAdmission }), [session, login, logout, admissionError, retryAdmission]);
    return <Context.Provider value={value}>{restoring ? <SessionLoading /> : children}</Context.Provider>;
  }

  function useSession(): SessionContextValue<S> {
    const context = useContext(Context);
    if (!context) throw new Error('useSession must be used inside its SessionProvider.');
    return context;
  }

  return { SessionProvider, useSession };
}
